/**
 * reach-probe.js — "did this browser actually let an ad/tracker request reach
 * the network?" (the file name is deliberately neutral: a script called
 * adblock-detect.js is itself filtered by the lists it tries to measure —
 * same reasoning as entropy-scan.js / trust-probe.js).
 *
 * Public surface:
 *
 *   window.__reachProbe.run({ timeoutMs }) -> Promise<{
 *     verdict: 'blocking' | 'none' | 'inconclusive',
 *     signals: [{ id, state:'clear'|'blocked'|'suspect'|'unknown'|'skip', detail, ms }],
 *     blocked: n, clear: n,
 *   }>
 *
 * Consumers: /privacy-audit (the person asked) and __showInitError in
 * timbrica-core.js (the processing engine failed to load — is a blocker the
 * reason?). It is NOT run on page load any more: the site-wide banner that did
 * that was removed 2026-09-02, see the note in timbrica-core.js.
 *
 * WHY THIS EXISTS — the load event is not evidence (measured 2026-09-01).
 *
 * The previous detector appended <script src="...adsbygoogle.js"> and read
 * onload/onerror. That answers "did something execute", not "did the real
 * script arrive". Every modern blocker ships *surrogates* — neutered stubs
 * served in place of famous ad scripts so pages do not break — and a surrogate
 * fires onload. Measured on real extensions in fresh profiles (uBO/AdGuard/ABP
 * XPIs from AMO, uBO Lite from GitHub releases; harness in tests/playwright/):
 *
 *   probe                        clean      uBO 1.74   AdGuard 5.5  ABP 4.43   uBOL(MV3)
 *   script adsbygoogle.js        onload     ONLOAD     onerror      onerror    ONLOAD
 *   - its resource-timing entry  dur 428ms  ABSENT     dur 0        dur 0      ABSENT
 *   HEAD adsbygoogle.js (cors)   CL 57154   throws     200, CL null throws     200, CL null
 *   GET  adsbygoogle.js (cors)   165121 B   throws     4144 B       throws     512 B
 *   GET  ad_status.js (cors)     29 B ok    throws     throws       throws     29 B, EXACT
 *
 * So uBlock Origin — the most used blocker — was reported as "no ad blocker
 * detected" by the old probe (prod bug report on /en/privacy-audit,
 * 2026-08-31), and uBO Lite reproduces ad_status.js byte-for-byte, so no
 * single endpoint can be trusted either.
 *
 * What survives every blocker in the table is *verifying the payload*: a
 * surrogate's whole purpose is to not be the real script, so it is always
 * orders of magnitude smaller (512 B ... 4 KB against 57 KB gzipped). Hence:
 *
 *   sdk_bytes  — HEAD the ad SDK and compare Content-Length with the real
 *                file; if that header is missing or small, escalate to GET and
 *                measure the body. This is the ARBITER: real bytes on record
 *                => 'clear'; a stub-sized body or a refused request =>
 *                'blocked'; our own deadline => 'unknown'.
 *   sdk_script — the SDK <script> the page already loaded for its own ad
 *                slots, when present (free, no second execution of an ad SDK);
 *                otherwise one we load. Its Resource Timing entry PROVES reach
 *                only when it carries the real file's size; anything less is
 *                'suspect' and merely sends the question to sdk_bytes.
 *                onerror is a refusal => 'blocked'.
 *
 * plus a same-origin control request, because "nothing reached the network"
 * must not be reported as "you have a blocker" when the network is simply
 * down. Offline / failed control => 'inconclusive', never 'none'.
 *
 * WHAT THE FIRST DAY IN PRODUCTION TAUGHT (36 h of verdicts, 2026-09-02):
 *
 *   · A cache hit is not an interception. In Chromium a cached real SDK has
 *     transferSize 0 and duration 0 — the exact shape the first version read
 *     as "extension redirect" — while decodedBodySize still says 204 542.
 *     Result: 7 500 of 13 085 RU sessions got "no blocker" on their first page
 *     and "blocker" on the next; `sdk_bytes:clear … sdk_script:blocked` rows
 *     (the arbiter said clear, the heuristic overruled it) were common. Now
 *     sizes decide, and a heuristic can never overrule a verified payload.
 *   · A timeout is not evidence. Every blocker in the table answers at once
 *     (cancel or surrogate); what times out is a slow or throttled route.
 *     45% of RU "blocking" verdicts were `head_AbortError|timeout` — Google's
 *     ad host probed from Russia, where it is throttled by the state — because
 *     pages without an ad slot fell back to the Google SDK regardless of the
 *     provider the host actually uses. The layout now names the provider
 *     (window.__reachProbeSdk) and a timeout is 'unknown', never 'blocked'.
 *     The price: a DNS sinkhole that DROPS instead of refusing reads as
 *     'inconclusive' rather than 'blocking'. The common ones (Pi-hole,
 *     AdGuard DNS, NextDNS) answer 0.0.0.0 and refuse instantly — still caught.
 *
 * Wording note: the verdict is about content blocking being active, not about
 * a particular extension. A Pi-hole, a corporate proxy or a DNS blocklist
 * produce the same measurement and the same practical result for the user.
 */
(function () {
    'use strict';
    if (window.__reachProbe) return;

    // Real SDK sizes measured 2026-09-01: adsbygoogle.js 57 154 B gzipped /
    // 165 121 B decoded (204 542 B by 2026-09-02); yandex context.js 110 KB /
    // 423 KB. Every surrogate measured was under 5 KB, so 20 KB sits an order
    // of magnitude from both — one threshold for headers, timing sizes and
    // (with margin, 50 KB) streamed bodies.
    var MIN_HEADER_BYTES = 20000;
    var MIN_BODY_BYTES = 50000;

    /* Provider → SDK. The same two URLs tool-ad-slot.js loads for real slots;
       ReachProbeProviderConventionTest keeps them from drifting apart. */
    var SDK_BY_PROVIDER = {
        adsense: 'https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js',
        yandex:  'https://yandex.ru/ads/system/context.js'
    };

    function now() {
        return (window.performance && performance.now) ? performance.now() : Date.now();
    }

    function bust(url) {
        return url + (url.indexOf('?') === -1 ? '?' : '&') + 'r=' + Date.now().toString(36) + Math.random().toString(36).slice(2, 7);
    }

    function stripQuery(url) {
        var i = String(url).indexOf('?');
        return i === -1 ? String(url) : String(url).slice(0, i);
    }

    function timingFor(url) {
        try {
            var list = performance.getEntriesByName(url);
            return list.length ? list[list.length - 1] : null;
        } catch (_) { return null; }
    }

    /* The SDK <script> the page loaded for its own ad slots, if any. */
    function pageSdkScript() {
        return document.getElementById('adsbygoogle-js')
            || document.getElementById('ya-context-js')
            || null;
    }

    /* The page's own tag wins (its src is the truth). Without a tag — pages
       with no ad slot — the layout names this host's provider in
       window.__reachProbeSdk (a provider key, or a full URL for tests). */
    function sdkUrl() {
        var el = pageSdkScript();
        if (el && el.src) return stripQuery(el.src);
        var hint = window.__reachProbeSdk;
        if (hint) return SDK_BY_PROVIDER[hint] || stripQuery(hint);
        return SDK_BY_PROVIDER.adsense;
    }

    /* What a Resource Timing entry proves. Both providers send
       Timing-Allow-Origin: *, so Chromium exposes body sizes — for a cache hit
       too (measured 2026-09-02: transferSize 0, duration 0, decodedBodySize
       204 542). Firefox reports 0/0/0 for cross-origin bodies even on a real
       load, so there an entry is never proof, only a reason to ask sdk_bytes.
         'clear'   — the real file's size is on record
         'suspect' — anything else: cache hit, extension redirect (a 4 144-byte
                     surrogate that took 26 ms — the shape that fooled the
                     previous "moved bytes or took time" rule on Edge+AdGuard)
                     and hidden sizes all look alike here */
    function entryVerdict(e) {
        var body = Math.max(e.decodedBodySize || 0, e.encodedBodySize || 0);
        return body >= MIN_HEADER_BYTES ? 'clear' : 'suspect';
    }

    function control(timeoutMs) {
        var t0 = now();
        var deadline = withDeadline(timeoutMs * 2);
        // Any HTTP answer counts, 404 included: the question is "is the network
        // answering us at all", not "does this file exist".
        return fetch(bust(location.origin + '/favicon.ico'), { method: 'HEAD', cache: 'no-store', signal: deadline.signal })
            .then(function () { deadline.cancel(); return { id: 'control', state: 'clear', detail: 'reachable', ms: Math.round(now() - t0) }; })
            .catch(function (e) { deadline.cancel(); return { id: 'control', state: 'blocked', detail: (e && e.name) || 'error', ms: Math.round(now() - t0) }; });
    }

    /* fetch() has no timeout of its own, and a blackholed request hangs
       forever — without this the whole audit would wait on it and never
       render. Our own abort is reported as 'unknown', not as blocking. */
    function withDeadline(ms) {
        var ctl = null;
        try { ctl = new AbortController(); } catch (_) { return { signal: undefined, cancel: function () {} }; }
        var t = setTimeout(function () { try { ctl.abort(); } catch (_) {} }, ms);
        return { signal: ctl.signal, cancel: function () { clearTimeout(t); } };
    }

    function failState(e) {
        return (e && e.name === 'AbortError') ? 'unknown' : 'blocked';
    }

    /* Read only as far as the answer. The RU provider (yandex context.js) sends
       no Content-Length at all, so the escalation below always runs there — and
       downloading 423 KB to learn that the first 50 KB are real is a bill paid
       by the visitor's phone. Cancelling the stream at the threshold measured
       59 KB instead of 423 KB (2026-09-01). Under a blocker the body ends after
       ~1 KB by itself. */
    function readEnough(response) {
        if (!response.body || !response.body.getReader) {
            return response.text().then(function (t) { return t.length; });
        }
        var reader = response.body.getReader();
        var total = 0;
        return (function pump() {
            return reader.read().then(function (chunk) {
                if (chunk.done) return total;
                total += (chunk.value && chunk.value.byteLength) || 0;
                if (total >= MIN_BODY_BYTES) {
                    try { reader.cancel(); } catch (_) {}
                    return total;
                }
                return pump();
            });
        })();
    }

    function sdkBytes(url, timeoutMs) {
        var t0 = now();
        var deadline = withDeadline(timeoutMs * 2);
        function done(state, detail) {
            deadline.cancel();
            return { id: 'sdk_bytes', state: state, detail: detail, ms: Math.round(now() - t0) };
        }

        return fetch(bust(url), { method: 'HEAD', mode: 'cors', cache: 'no-store', credentials: 'omit', signal: deadline.signal })
            .then(function (r) {
                var cl = parseInt(r.headers.get('content-length') || '', 10);
                if (cl >= MIN_HEADER_BYTES) return done('clear', 'content_length_' + cl);
                // Header missing or implausibly small: either a surrogate
                // (measured: AdGuard and uBO Lite answer HEAD with no
                // Content-Length) or a proxy that chunks the response. Only
                // this branch pays for a body, and under a blocker that body
                // is about a kilobyte.
                return fetch(bust(url), { mode: 'cors', cache: 'no-store', credentials: 'omit', signal: deadline.signal })
                    .then(readEnough)
                    .then(function (n) {
                        return n >= MIN_BODY_BYTES
                            ? done('clear', 'body_' + n)
                            : done('blocked', 'stub_body_' + n);
                    })
                    .catch(function (e) { return done(failState(e), 'get_' + ((e && e.name) || 'error')); });
            })
            .catch(function (e) { return done(failState(e), 'head_' + ((e && e.name) || 'error')); });
    }

    /* Passive form: the page already has the SDK <script>. No extra request and
       no second execution of an ad SDK — just ask whether its bytes are on
       record. Anything short of proof is 'suspect' and sdk_bytes decides. */
    function sdkScriptPassive(el) {
        var t0 = now();
        var e = timingFor(el.src);
        if (!e) {
            // Absence can also mean "still in flight". Only treat it as a
            // question once the document has finished loading.
            if (document.readyState !== 'complete') return null;
            return { id: 'sdk_script', state: 'suspect', detail: 'page_sdk_no_timing', ms: Math.round(now() - t0) };
        }
        var v = entryVerdict(e);
        return { id: 'sdk_script', state: v, detail: 'page_sdk_' + (v === 'clear' ? Math.round(e.duration) + 'ms' : 'unverified'), ms: Math.round(now() - t0) };
    }

    function sdkScriptActive(url, timeoutMs) {
        return new Promise(function (resolve) {
            var t0 = now();
            var src = bust(url);
            var s = document.createElement('script');
            var settled = false;
            function finish(state, detail) {
                if (settled) return;
                settled = true;
                try { s.parentNode && s.parentNode.removeChild(s); } catch (_) {}
                resolve({ id: 'sdk_script', state: state, detail: detail, ms: Math.round(now() - t0) });
            }
            s.async = true;
            s.src = src;
            s.onerror = function () { finish('blocked', 'onerror'); };
            s.onload = function () {
                var e = timingFor(src);
                if (!e) return finish('suspect', 'onload_no_timing');
                var v = entryVerdict(e);
                finish(v, v === 'clear' ? 'onload_' + Math.round(e.duration) + 'ms' : 'onload_unverified');
            };
            setTimeout(function () { finish('unknown', 'timeout'); }, timeoutMs || 4000);
            document.head.appendChild(s);
        });
    }

    /* Проба СТУЧИТСЯ в рекламный хост провайдера (по построению — иначе она
       ничего не измеряет). Клиенту, которого общий гейт уже признал машиной,
       этот стук не нужен: ответ «у вас блокировщик» адресован человеку, а
       машине адресовать нечего. Замер 18.09.2026: на intl проба идёт 4–19 раз
       в сутки (39 в день фермы) против ~1 300 законных рекламных запросов —
       величина малая, но это ЕДИНСТВЕННАЯ оставшаяся дверь к pagead2 в обход
       гейта, а инвариант «машина не обращается к рекламной инфраструктуре»
       дороже одной двери. Гейта нет на странице (реклама скрыта у платных) —
       работаем как раньше: это фича для человека, а не рекламный запрос. */
    function machineSkip() {
        if (typeof window.__adTrafficSkip !== 'function') return Promise.resolve(null);
        return new Promise(function (resolve) { window.__adTrafficSkip(resolve); });
    }

    function run(opts) {
        return machineSkip().then(function (reason) {
            if (!reason) return runProbe(opts);
            return {
                verdict: 'inconclusive',
                signals: [{ id: 'gate', state: 'skip', detail: reason, ms: 0 }],
                blocked: 0, clear: 0, sdk: sdkUrl(), offline: false
            };
        });
    }

    function runProbe(opts) {
        opts = opts || {};
        var url = sdkUrl();
        var timeoutMs = opts.timeoutMs || 4000;

        var passive = null;
        var el = pageSdkScript();
        if (el && el.src) passive = sdkScriptPassive(el);

        /* When the page's own ad SDK is already on the page and its bytes are
           on record, the question is answered and a second request would only
           spend the visitor's data to agree with itself. Every other case —
           surrogate, block, cache hit with hidden sizes, no SDK on the page —
           pays for the payload check, which is exactly where it is needed. */
        var passiveClear = passive && passive.state === 'clear';
        var jobs = [
            control(timeoutMs),
            passiveClear
                ? Promise.resolve({ id: 'sdk_bytes', state: 'skip', detail: 'page_sdk_already_clear', ms: 0 })
                : sdkBytes(url, timeoutMs),
            passive ? Promise.resolve(passive) : sdkScriptActive(url, timeoutMs)
        ];

        return Promise.all(jobs).then(function (signals) {
            var byId = {};
            signals.forEach(function (s) { byId[s.id] = s; });

            var offline = (navigator.onLine === false);
            var controlBad = offline || byId.control.state === 'blocked';

            var evidence = signals.filter(function (s) { return s.id !== 'control' && s.state !== 'skip'; });
            var blocked = evidence.filter(function (s) { return s.state === 'blocked'; }).length;
            var clear = evidence.filter(function (s) { return s.state === 'clear'; }).length;

            /* Proof beats refusal beats silence. A verified payload (real bytes
               on record) is the strongest fact we can have, so it settles the
               verdict even when a heuristic elsewhere looked odd; a refusal
               (stub body, network error, onerror) is a fact about this browser
               only when our own network is answering; 'suspect' and 'unknown'
               never accuse. */
            var verdict;
            if (clear) verdict = 'none';
            else if (blocked) verdict = controlBad ? 'inconclusive' : 'blocking';
            else verdict = 'inconclusive';

            var out = {
                verdict: verdict,
                signals: signals,
                blocked: blocked,
                clear: clear,
                sdk: url,
                offline: offline
            };

            try {
                if (window.__ae) window.__ae('feature_use', {
                    f: 'reach_probe',
                    verdict: verdict,
                    blocked: blocked,
                    clear: clear,
                    detail: evidence.map(function (s) { return s.id + ':' + s.state + ':' + s.detail; }).join('|').slice(0, 180)
                });
            } catch (_) {}

            return out;
        });
    }

    window.__reachProbe = { run: run, VERSION: 2 };

    if (typeof window.__drainSharedModule === 'function') {
        try { window.__drainSharedModule('__reachProbe'); } catch (_) {}
    }
})();
